import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import useAuth from '../hooks/useAuth';
import Unauthorized from './Unauthorized';

/**
 * SECURITY NOTE:
 * This component uses localStorage token ONLY for client-side routing/UX decisions.
 * The actual API authentication uses httpOnly cookies (set by backend).
 * Even if localStorage token is stolen via XSS, it cannot be used for API calls
 * because the backend prioritizes the secure httpOnly cookie.
 */
function RequireAuth({ allowedRoles }) {
  const { auth } = useAuth();
  const location = useLocation();

  let data = null;
  let isTokenValid = false;

  if (localStorage.getItem('loginToken')) {
    try {
      const token = localStorage.getItem('loginToken');
      data = jwtDecode(token);

      // Check if token is expired
      const currentTime = Date.now() / 1000; // Convert to seconds
      if (data.exp && data.exp < currentTime) {
        // Token expired, remove it
        localStorage.removeItem('loginToken');
        console.log('Token expired, redirecting to login');
        return (
          <Navigate
            to="/login"
            state={{ from: location, expired: true }}
            replace
          />
        );
      }

      isTokenValid = true;
    } catch (error) {
      // Invalid token, remove it
      console.error('Invalid token:', error);
      localStorage.removeItem('loginToken');
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
  }

  // No token at all
  if (!data) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user has allowed role
  if (allowedRoles.includes(data.role)) {
    return <Outlet />;
  }

  // User is authenticated but doesn't have the right role
  if (auth?.email || data?.email) {
    return <Unauthorized />;
  }

  // Fallback to login
  return <Navigate to="/login" state={{ from: location }} replace />;
}

export default RequireAuth;
